import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Lock, Search, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch {
        return "—";
    }
}

export default function AdminPage() {
    const [token, setToken] = useState(() => sessionStorage.getItem("mj_admin_token") || "");
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [data, setData] = useState({ total: 0, count: 0, subscribers: [] });
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        document.title = "Admin — Mashi J";
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex, nofollow";
        document.head.appendChild(meta);
        return () => document.head.removeChild(meta);
    }, []);

    const load = useCallback(async (t, q = "") => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/subscribers?q=${encodeURIComponent(q)}`, {
                headers: { Authorization: `Bearer ${t}` },
            });
            setData(res.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                sessionStorage.removeItem("mj_admin_token");
                setToken("");
            } else {
                toast.error(err?.response?.data?.detail || "Could not load subscribers.");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (token) load(token, query);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, query]);

    const login = async (e) => {
        e.preventDefault();
        setLoginError("");
        setLoggingIn(true);
        try {
            const res = await axios.post(`${API}/api/admin/login`, { password });
            sessionStorage.setItem("mj_admin_token", res.data.token);
            setToken(res.data.token);
            setPassword("");
        } catch (err) {
            setLoginError(err?.response?.data?.detail || "Login failed.");
        } finally {
            setLoggingIn(false);
        }
    };

    const remove = async (id, email) => {
        if (!window.confirm(`Delete ${email} from the list?`)) return;
        try {
            await axios.delete(`${API}/api/admin/subscribers/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Deleted.");
            load(token, query);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed.");
        }
    };

    const exportCsv = async () => {
        try {
            const res = await axios.get(`${API}/api/admin/subscribers.csv`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "mashi-j-subscribers.csv";
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Export failed.");
        }
    };

    const logout = () => {
        sessionStorage.removeItem("mj_admin_token");
        setToken("");
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-garden text-blush font-body flex items-center justify-center px-5">
                <form onSubmit={login} className="w-full max-w-sm border border-olive/40 p-8 md:p-10" noValidate>
                    <span className="inline-flex items-center justify-center h-12 w-12 rounded-full border border-olive/40 text-tulip mb-6">
                        <Lock className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl mb-2">Admin</h1>
                    <p className="text-sm text-blush/60 mb-8">Mashi J — subscriber list.</p>
                    <label htmlFor="admin-password" className="sr-only">Admin password</label>
                    <input
                        id="admin-password"
                        type="password"
                        autoComplete="current-password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="admin-login-input"
                        className="w-full bg-transparent border border-olive/40 px-5 py-4 text-blush placeholder-blush/35 focus:border-tulip focus:outline-none transition-colors duration-200"
                    />
                    {loginError && (
                        <p data-testid="admin-login-error" className="mt-4 text-sm text-honeysuckle">{loginError}</p>
                    )}
                    <button
                        type="submit"
                        disabled={loggingIn}
                        data-testid="admin-login-submit"
                        className="mt-6 w-full bg-rose text-blush px-6 py-4 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-tulip hover:text-garden disabled:opacity-50 transition-colors duration-300"
                    >
                        {loggingIn ? "Checking…" : "Log In"}
                    </button>
                    <a href="/" className="mt-8 inline-block text-[10px] uppercase tracking-[0.2em] text-blush/40 hover:text-tulip transition-colors">← Back to site</a>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-garden text-blush font-body px-5 md:px-10 py-10 md:py-14">
            <div className="max-w-5xl mx-auto">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
                    <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl md:text-4xl">Subscribers</h1>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            data-testid="admin-export-csv-btn"
                            onClick={exportCsv}
                            className="inline-flex items-center gap-2 bg-rose text-blush px-5 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-tulip hover:text-garden transition-colors duration-300"
                        >
                            <Download className="h-4 w-4" aria-hidden="true" /> Export CSV
                        </button>
                        <button
                            type="button"
                            data-testid="admin-logout-btn"
                            onClick={logout}
                            className="text-[11px] uppercase tracking-[0.18em] text-blush/60 hover:text-tulip transition-colors"
                        >
                            Log out
                        </button>
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <p data-testid="admin-total-count" className="text-sm text-blush/70">
                        Total signups: <span className="font-display font-bold text-xl text-blush tabular-nums">{data.total}</span>
                    </p>
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-blush/40" aria-hidden="true" />
                        <label htmlFor="admin-search" className="sr-only">Search subscribers</label>
                        <input
                            id="admin-search"
                            type="search"
                            placeholder="Search email or name…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            data-testid="admin-search-input"
                            className="w-64 bg-transparent border border-olive/40 pl-11 pr-4 py-3 text-sm text-blush placeholder-blush/35 focus:border-tulip focus:outline-none transition-colors duration-200"
                        />
                    </div>
                </div>

                <div className="border border-olive/30 overflow-x-auto">
                    <table data-testid="admin-table" className="w-full text-sm min-w-[640px]">
                        <thead>
                            <tr className="border-b border-olive/30 text-left text-[10px] uppercase tracking-[0.2em] text-blush/50">
                                <th className="px-4 md:px-5 py-4 font-medium">Email</th>
                                <th className="px-4 md:px-5 py-4 font-medium">Date</th>
                                <th className="px-4 md:px-5 py-4 font-medium">Consent</th>
                                <th className="px-4 md:px-5 py-4"><span className="sr-only">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.subscribers.map((s) => (
                                <tr key={s.id} className="border-b border-olive/20 last:border-0">
                                    <td className="px-4 md:px-5 py-4 break-all" data-testid={`admin-row-email-${s.id}`}>{s.email}</td>
                                    <td className="px-4 md:px-5 py-4 text-blush/70 whitespace-nowrap">{formatDate(s.created_at)}</td>
                                    <td className="px-4 md:px-5 py-4">
                                        {s.consent ? (
                                            <span className="text-lily text-xs uppercase tracking-[0.15em]">Yes</span>
                                        ) : (
                                            <span className="text-porcelain text-xs uppercase tracking-[0.15em]">No</span>
                                        )}
                                    </td>
                                    <td className="px-4 md:px-5 py-4 text-right">
                                        <button
                                            type="button"
                                            data-testid={`admin-delete-btn-${s.id}`}
                                            aria-label={`Delete ${s.email}`}
                                            onClick={() => remove(s.id, s.email)}
                                            className="inline-flex items-center justify-center h-9 w-9 border border-olive/30 text-blush/60 hover:text-garden hover:bg-tulip hover:border-tulip transition-colors duration-200"
                                        >
                                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {!loading && data.subscribers.length === 0 && (
                                <tr>
                                    <td colSpan="4" className="px-5 py-10 text-center text-blush/50">
                                        {query ? "No matches." : "No signups yet."}
                                    </td>
                                </tr>
                            )}
                            {loading && (
                                <tr>
                                    <td colSpan="5" className="px-5 py-10 text-center text-blush/50">Loading…</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <p className="mt-4 text-xs text-blush/40">
                    Showing {data.count} of {data.total} · newest first
                </p>
                <a href="/" className="mt-8 inline-block text-[10px] uppercase tracking-[0.2em] text-blush/40 hover:text-tulip transition-colors">← Back to site</a>
            </div>
        </div>
    );
}
